import { useGetCommerce } from '@pages/Commerce/hooks/commerce';
import { useEffect } from 'react';
import { useElectron } from './useElectron';
import { useFetchCallback } from '@services/services';
import { isTransientAuthColdStart, waitForAuthReady } from '@services/authReady';
import * as Sentry from '@sentry/react';

/**
 * Reporta al backend la versión instalada de la app de escritorio (`electron_app_version`).
 * Se monta en `Layout.tsx` y su cuerpo solo corre dentro del shell de Electron
 * (`isElectronInstance`), así que es el único efecto del arranque exclusivo de Windows.
 *
 * ⚠ Esto es TELEMETRÍA: que falle NO puede costarle la sesión al operador.
 *
 * Hasta 2026-09 el `catch` llamaba `auth.logout()` ante CUALQUIER error — el `invoke` del IPC o
 * el `PUT /v1/comercio`. Como este hook es el único gateado por `isElectronInstance`, ese `catch`
 * era el ÚNICO camino de cierre de sesión que solo existe en la app de Windows, y convertía
 * "falló reportar la versión de la app" en "vuelve a escribir usuario y contraseña".
 *
 * Por qué empezó a doler sin que este archivo cambiara (3 commits, todos de diciembre de 2023):
 * el arranque en frío creció alrededor. `2f8d6e12b` (2026-06-23) documentó que las cargas de mount
 * pueden dispararse antes de que `checkAuth` restaure el token → el authorizer responde 401/403; y
 * `5b8673f28` que la avalancha de fetches lo throttlea → 429 → 500 en ráfaga. Ese fix inmunizó a
 * los consumidores vía `isTransientAuthColdStart` (`services.ts:181` relanza el error transitorio
 * ANTES del `auth.logout()` de `:192`, justamente para no cerrar una sesión válida por una carrera
 * de arranque). Este hook nunca se adaptó: su `catch` pelón atrapaba ese mismo error relanzado y sí
 * deslogueaba. Ver `docs/plan-fases-app-windows-electron.md` (F1.1).
 */
export const useInitElectronVersion = () => {
  const { commerce } = useGetCommerce();
  const { isElectronInstance, ipcRenderer } = useElectron();
  const { call: setVersion } = useFetchCallback<{ electron_app_version: string | null }>('put', '/v1/comercio');

  const validateElectronVersion = async () => {
    try {
      const version = (await ipcRenderer.invoke('getAppVersion')) as string;
      const installedVersion = commerce?.electron_app_version;
      if (!version || version === installedVersion) {
        return;
      }

      // `commerce` sale del store PERSISTIDO (`useGetCommerce` lee de Redux y solo hace el GET si
      // no hay `id`), así que al reabrir la app este efecto puede correr ANTES de que `checkAuth`
      // restaure el token. Ese PUT saldría sin `Authorization` → 401. Y por no ser idempotente, el
      // interceptor tampoco lo reintenta ante 429/500 (`axiosInstance.ts:199` acota ese retry a
      // GET/HEAD para no duplicar mutaciones). Esperar brevemente al token evita la request
      // condenada, en vez de limitarse a manejar su fallo. Resuelve síncrono si el token ya está.
      if (!(await waitForAuthReady())) {
        return;
      }

      await setVersion({ data: { electron_app_version: version } });
    } catch (error) {
      // Transitorio de arranque: no es un fallo real y la siguiente carga lo reporta. Ni Sentry.
      if (isTransientAuthColdStart(error)) {
        return;
      }

      Sentry.withScope((scope) => {
        scope.setTag('electron_version_report', 'failed');
        scope.setFingerprint(['electron', 'version-report']);
        Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
      });
    }
  };

  useEffect(() => {
    if (commerce && isElectronInstance && commerce.id) {
      validateElectronVersion();
    }
  }, [commerce, isElectronInstance]);
};
